'use client';

import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import Step from '@mui/material/Step';
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpectJobTitle from '@/components/resume/resume-evaluation/ExpectJobTitle';
import UploadResume from '@/components/resume/resume-upload/UploadResume';
import CreateResume from './CreateResume';
import LoginModal from '@/components/login/LoginModal';
import ExitSurveyModal from './ExitSurveyModal';
import GlobalContext from '@/context/global-context';
import { OptionType } from '@/types/common';
import { QUERY_CREATE, QUERY_REDIRECT } from '@/constants/auth';
import {
  RESUME_EVALUATION_LOADING_ROUTER_PATH,
  RESUME_EVALUATION_ROUTER_PATH,
} from '@/constants/router-path';
import { EVALUATION_INTRO_ID } from '@/constants/component-id';
import { STORAGE_KEY_VALUE } from '@/constants/job-title';
import setAlertValue from '@/components/Alert';
import { RESUME_EVALUATION_GROUP } from '@/constants/site-track';
import { isValidUUID } from '@/utils/common-utils';
import { saveDBFile } from '@/utils/storage';
import { uploadEvaluationResume } from '@/api/tanzhi';
import TopIntroImage from '@/assets/resume-evaluation/top-intro.png';

const EVALUATION_STEPS = ['填写目标岗位', '上传简历', '解锁诊断报告'];

const EvaluationIntro = ({ isLogin }: { isLogin: boolean }) => {
  const [resumeFile, setResumeFile] = React.useState<File | null>(null);
  const [evaluationStep, setEvaluationStep] = React.useState(0);
  const [expectJobTitle, setExpectJobTitle] = React.useState<OptionType | null>(
    null,
  );
  const [loginModalOpen, setLoginModalOpen] = React.useState(false);
  const router = useRouter();

  const handleNextStep = (jobTitle: OptionType) => {
    setExpectJobTitle(jobTitle);
    setEvaluationStep(1);
  };

  React.useEffect(() => {
    const referer = GlobalContext.getContext('referer');
    if (referer) {
      const pathname = new URL(referer).pathname;
      if (
        /^\/job-titles\/[0-9a-fA-F-]{36}/.test(pathname) ||
        /^\/(?!.*(job-titles|jianli|job-detail|companies)).+\/[0-9a-fA-F-]{36}\//.test(
          pathname,
        )
      ) {
        const storageValue = localStorage.getItem(STORAGE_KEY_VALUE);
        const jobTitle = storageValue ? JSON.parse(storageValue) : null;
        jobTitle &&
          jobTitle.id &&
          jobTitle.name &&
          isValidUUID(jobTitle.id) &&
          setExpectJobTitle(jobTitle);
      }
    }
  }, []);

  React.useEffect(() => {
    if (resumeFile && expectJobTitle) {
      if (isLogin) {
        uploadEvaluationResume(expectJobTitle, resumeFile)
          .then((res) => {
            const data = res.data;
            const { id } = data;
            router.push(`${RESUME_EVALUATION_LOADING_ROUTER_PATH}?id=${id}`);
          })
          .catch((err) =>
            setAlertValue({
              msg: err.response.data?.detail || '诊断简历失败，请重试！',
            }),
          );
      } else {
        (async () => {
          await saveDBFile('resume_file', resumeFile);
          await saveDBFile('expect_job_title', expectJobTitle);
          setLoginModalOpen(true);
        })();
      }
    }
  }, [resumeFile]);

  return (
    <>
      <div
        className="bg-web-earth-100/35"
        data-ele-id="1st_screen"
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-observer-id="1st_screen"
      >
        <div className="mx-auto px-5 max-w-[1200px] flex justify-between items-center flex-row">
          <div className="tablet:max-w-[484px]">
            <p className="text-4.5xl font-semibold">你的简历真的够好吗？</p>
            <p className="mt-4 text-xl">
              HR 平均只花 30
              秒决定你值不值得面试。你的简历抓得住他们的注意力吗？我们用数据和经验，帮你读懂招聘标准、发现表达短板，让能力不再被埋没。
            </p>
            <div className="text-web-grey-600 mt-24">
              <p className="flex items-center mb-2.5">
                <CheckCircleRoundedIcon className="!text-base text-web-green-700 mr-2" />
                <span>面试机会预计提升 25%</span>
              </p>
              <p className="flex items-center">
                <ThumbUpIcon className="!text-base text-web-green-700 mr-2" />
                <span>已为 5,000+ 用户提供服务</span>
              </p>
            </div>
          </div>
          <div className="w-full mt-4 tablet:mt-0 tablet:max-w-[570px]">
            <Image
              src={TopIntroImage}
              alt=""
              style={{
                objectFit: 'contain',
              }}
              priority
            />
          </div>
        </div>
      </div>
      <div
        className="bg-web-blue-50"
        id={EVALUATION_INTRO_ID}
        data-ele-id="2nd_screen"
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-observer-id="2nd_screen"
      >
        <div className="mx-auto py-12 px-5 max-w-[784px]">
          <div className="text-center mb-12">
            <p className="text-4.5xl font-semibold mb-4">立即开始诊断</p>
            <p className="text-xl">三个简单步骤，获得专业级简历优化方案</p>
          </div>
          <Stepper activeStep={evaluationStep}>
            {EVALUATION_STEPS.map((label, index) => (
              <Step
                key={label + index}
                disabled={false}
                className="!text-white"
              >
                <StepLabel
                  StepIconProps={{
                    className:
                      '!text-4pxl !text-white border rounded-full border-slate-300 [&.Mui-active]:!text-web-blue-500 [&.Mui-completed]:!text-web-blue-500 [&.Mui-active]:border-blue-400 [&.Mui-active_.MuiStepIcon-text]:!fill-white [&_.MuiStepIcon-text]:!fill-black/80',
                  }}
                  componentsProps={{
                    label: {
                      className:
                        '!text-primary !text-lg !font-normal tablet:!text-base',
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <div className="mt-10 bg-white rounded-xl">
            {evaluationStep === 0 ? (
              <div className="px-[150px] pt-[70px] pb-[108px] rounded-xl border border-web-grey-50">
                <ExpectJobTitle
                  value={expectJobTitle}
                  onNextStep={handleNextStep}
                  needResumeFile={false}
                  autoCompleteClassName="w-full mb-10 border rounded-lg !pb-0"
                  smallSize={true}
                  submitButtonClassName="w-full !h-[42px] !ml-auto tablet:!w-25"
                  title={
                    <>
                      <p className="text-xl font-semibold mb-2">目标岗位</p>
                      <p className="mb-10">
                        明确你的求职方向，让诊断更具针对性，助你精准提升
                      </p>
                    </>
                  }
                  dataEleGroup={RESUME_EVALUATION_GROUP}
                />
              </div>
            ) : (
              <div className="py-4 px-48 text-center">
                <p className="text-xl font-semibold mb-4.5">上传简历</p>
                <UploadResume
                  setResumeFile={setResumeFile}
                  setEvaluationStep={setEvaluationStep}
                  className="px-5 py-[30px]"
                  dataEleGroup={RESUME_EVALUATION_GROUP}
                />
                <p className="mt-6 text-sm text-web-grey-600">
                  还没准备好简历？
                  <CreateResume
                    isLogin={isLogin}
                    titleClassName="text-primary"
                    title="立即创建"
                  />
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isLogin && (
        <LoginModal
          isOpen={loginModalOpen}
          searchParams={
            new URLSearchParams({
              [QUERY_REDIRECT]: RESUME_EVALUATION_ROUTER_PATH,
              [QUERY_CREATE]: 'evaluation',
            })
          }
        />
      )}
      <ExitSurveyModal />
    </>
  );
};

export default EvaluationIntro;
